/**
 * ランダム表示を管理するクラス
 *
 * 仕様:
 * - この処理は最初に1回だけ実行される
 * - is_js__randomView がついた要素の子要素（任意の要素、例えば <picture> 要素など）の data-percent の値を取得
 * - 取得した値をもとにランダム表示する
 * - 合計が1となることを想定しているが、1にならなかった場合は、合計値から比率を計算する
 * - どれを表示するか決定したら、その要素に ist-js__randomView--active を付与する
 *
 * サンプル html:
 * <div class="ist-homeKeyvisual__image is_js__randomView">
 *   <picture
 *     class="ist-homeKeyvisual__imagePicture ist-homeKeyvisual__imagePicture--a1"
 *     data-percent="0.2"
 *   >
 *     <img src="/images/ist/kv-a1.jpg" alt="" />
 *   </picture>
 *   <div
 *     class="ist-homeKeyvisual__imagePicture ist-homeKeyvisual__imagePicture--a2"
 *     data-percent="0.3"
 *   >
 *     コンテンツA
 *   </div>
 *   <span
 *     class="ist-homeKeyvisual__imagePicture ist-homeKeyvisual__imagePicture--a3"
 *     data-percent="0.5"
 *   >
 *     コンテンツB
 *   </span>
 * </div>
 *
 * ※ 対象となる要素は必ずしも <picture> 要素に限らず、任意の要素で data-percent 属性があれば処理の対象となります。
 */
export class RandomView {
  private container: Element;

  constructor(container: Element) {
    this.container = container;
    this.init();
  }

  private init(): void {
    // ① 対象となる子要素を取得する（data-percent属性を持つ要素）
    const items: Element[] = Array.from(this.container.children).filter(
      (child) => child.hasAttribute('data-percent')
    );

    // 対象要素が存在しない場合は何もしない
    if (items.length === 0) {
      return;
    }

    // ② 各要素のdata-percent属性から数値を取得する
    const percents: number[] = items.map((item) => {
      const percentValue = parseFloat(item.getAttribute('data-percent') || '0');
      return isNaN(percentValue) ? 0 : percentValue;
    });

    // ③ 合計値を計算する
    const total = percents.reduce((acc, value) => acc + value, 0);

    // ④ 合計が1でなければ、各値を正規化する（合計が1になるようにする）
    const normalizedPercents: number[] =
      total !== 1 ? percents.map((p) => p / total) : percents;

    // ⑤ 0から1の間の乱数を生成する
    const randomValue = Math.random();

    // ⑥ 累積和を用いて、乱数が属する区間を判定する
    let cumulative = 0;
    let selectedIndex = 0;
    for (let i = 0; i < normalizedPercents.length; i++) {
      cumulative += normalizedPercents[i];
      if (randomValue < cumulative) {
        selectedIndex = i;
        break;
      }
    }

    // ⑦ 選ばれた要素にクラス ist-js__randomView--active を付与する
    items[selectedIndex].classList.add('ist-js__randomView--active');
  }
}

/**
 * 複数のランダム表示要素を管理するマネージャークラス
 */
export class RandomViewManager {
  constructor() {
    this.init();
  }

  private init(): void {
    // DOMの読み込みが完了しているか確認し、初期化する
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.initializeRandomViews();
      });
    } else {
      this.initializeRandomViews();
    }
  }

  private initializeRandomViews(): void {
    // クラス is_js__randomView を持つすべての要素を取得する
    const containers = document.querySelectorAll('.is_js__randomView');
    containers.forEach((container) => {
      // 各コンテナに対して RandomView の処理を実行する
      new RandomView(container);
    });
  }
}
